import { ProductType } from "@/common/course/types";
import type { BundleInfoMap, CartItem } from "@/utils/cartUtils";

/**
 * Formats course or program name with brand prefix if available
 */
export const formatCourseName = (
  courseName: string,
  brand?: string | null,
): string => {
  if (brand && brand !== "Unknown Brand") {
    return `${brand}: ${courseName}`;
  }
  return courseName;
};

/**
 * Maps each program's bundleId to its associated courses
 */
export const createProgramCoursesMap = (
  programItems: CartItem[],
  allItems: CartItem[],
): Map<string, CartItem[]> => {
  const map = new Map<string, CartItem[]>();
  programItems.forEach((program) => {
    if (program.bundleId) {
      const courses = allItems.filter(
        (item) => item.bundleId === program.bundleId,
      );
      map.set(program.bundleId, courses);
    }
  });
  return map;
};

/**
 * Determines if the cart has any program items
 */
export const hasProgram = (items: CartItem[]): boolean => {
  return items.some((item) => !!item.bundleId);
};

/**
 * Filters items to get only program items
 */
export const getProgramItems = (items: CartItem[]): CartItem[] => {
  return items.filter((item) => !!item.bundleId);
};

/**
 * Filters items to get only individual course items (not part of programs)
 */
export const getIndividualCourses = (
  items: CartItem[],
  bundleInfoMap: BundleInfoMap,
): CartItem[] => {
  // in case program api call failed, also include items that don't have corresponding bundle info
  const bundleIds = Object.keys(bundleInfoMap);
  return items.filter(
    (item) =>
      !item.bundleId ||
      !bundleIds.some((bundleId) => bundleId === item.bundleId),
  );
};

/**
 * Gets courses that belong to a specific program bundle
 */
export const getProgramCourses = (
  items: CartItem[],
  bundleId: string,
): CartItem[] => {
  return items.filter((item) => item.bundleId === bundleId);
};

/**
 * Gets the displayed courses for a program (none if collapsed, all if expanded)
 */
export const getDisplayedCourses = (
  items: CartItem[],
  bundleId: string,
  showAllCourses: Record<string, boolean>,
): CartItem[] => {
  const programCourses = getProgramCourses(items, bundleId);
  return showAllCourses[bundleId] ? programCourses : programCourses.slice(0, 0);
};

/**
 * Finds a matching partner for image display based on brand
 */
export const findMatchingPartner = (partners: any[], brand: string) => {
  if (!partners || partners.length === 0 || !brand) return null;

  return (
    partners.find(
      (partner) =>
        partner.key?.toLowerCase() === brand.toLowerCase() ||
        partner.name?.toLowerCase() === brand.toLowerCase(),
    ) || null
  );
};

/**
 * Processes program item data for display
 */
export const processProgramItem = (
  programItem: CartItem,
  items: CartItem[],
  showAllCourses: Record<string, boolean>,
  cartData: any,
) => {
  if (!programItem.bundleId) return null;

  const programCourses = getProgramCourses(items, programItem.bundleId);
  const displayedCourses = getDisplayedCourses(
    items,
    programItem.bundleId,
    showAllCourses,
  );
  const programBrandInfo =
    cartData?.enrichedProgramInfo?.[programItem.bundleId] || null;
  const matchingPartner = findMatchingPartner(
    programBrandInfo?.partners || [],
    programItem.brand || "",
  );
  const priceData = formatPriceDisplay(programItem);

  return {
    programCourses,
    displayedCourses,
    programBrandInfo,
    matchingPartner,
    priceData,
  };
};

/**
 * Formats price display data for cart items
 */
export const formatPriceDisplay = (item: CartItem) => {
  const hasDiscount =
    item.originalPrice && item.originalPrice > (item.price || 0);

  return {
    hasDiscount,
    originalPrice: item.originalPrice,
    currentPrice: item.price || 0,
    formattedOriginalPrice: item.originalPrice
      ? `$${item.originalPrice.toFixed(2)}`
      : null,
    formattedCurrentPrice: item.price ? `$${item.price.toFixed(2)}` : null,
  };
};

/**
 * Gets the cart badge configuration based on multi-cart rule and screen size
 */
export const getCartBadgeConfig = (
  shouldShowBadge: boolean,
  isMultiCartActive: boolean,
  cartItemCount: number,
) => {
  if (!shouldShowBadge) return null;

  if (isMultiCartActive) {
    return {
      desktop: {
        show: true,
        text: cartItemCount > 99 ? "99+" : cartItemCount.toString(),
        className:
          "hidden md:flex absolute top-0.5 right-1.9 -translate-y-1/2 translate-x-1/2 z-10 w-[13px] h-[13px] bg-red-500 text-white text-[10px] font-semibold rounded-full items-center justify-center flex-shrink-0",
      },
      mobile: {
        show: true,
        className:
          "md:hidden absolute top-1 right-1.5 -translate-y-1/2 z-10 w-2.5 h-2.5 bg-red-500 rounded-full",
      },
    };
  }

  // Default behavior: show dot on all screen sizes
  return {
    desktop: {
      show: false,
    },
    mobile: {
      show: true,
      className:
        "absolute top-1 right-1.5 -translate-y-1/2 z-10 w-2.5 h-2.5 bg-red-500 rounded-full",
    },
  };
};
